import { useState } from "react";
import { Bot, ChevronDown, ExternalLink, Sparkles } from "lucide-react";

import PixelAvatar from "@/shared/components/PixelAvatar";
import { Counted, ListCard, ListEmpty } from "@/shared/components/ListCard";
import { Note } from "@/shared/components/settings-atoms";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import Section from "./Section";

import type { ConfigSection } from "../ConfigSectionHelp";
import type { Agent, SubAgent } from "@/shared/types";

function SubAgentDialog({ sub, parentName, onOpenChange }: {
  sub: SubAgent | null;
  parentName: string;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={!!sub} onOpenChange={onOpenChange}>
      {sub && (
        <DialogContent className="p-0 gap-0 w-[480px] max-w-[94vw] rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
          <div className="px-5 pt-5 pb-4 flex items-center gap-3">
            <PixelAvatar seed={sub.seed} hue={sub.hue} size={36} rounded={9} className="shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)] truncate">{sub.name}</DialogTitle>
                <span className="shrink-0 h-[16px] px-[5px] rounded-[4px] ring-1 ring-inset ring-[var(--hairline)] text-[10px] font-mono text-[var(--text-tertiary)] flex items-center">{sub.model}</span>
              </div>
              <DialogDescription className="text-[13px] text-[var(--text-tertiary)] mt-0.5 truncate">{sub.desc}</DialogDescription>
            </div>
          </div>
          <div className="px-5 pb-5">
            <div className="flex items-center gap-1 mb-1.5 section-label">
              <Sparkles className="w-[9px] h-[9px]" strokeWidth={2} />
              Instructions · written by {parentName}
            </div>
            <div className="rounded-[10px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] px-3.5 py-3 max-h-[46vh] overflow-y-auto text-[13.5px] leading-[1.7] text-[var(--text-secondary)] whitespace-pre-wrap break-words tracking-[-0.005em]">
              {sub.instructions}
            </div>
          </div>
          <div className="px-5 py-3 border-t hairline bg-[var(--surface)] flex items-center gap-4 text-[11.5px] text-[var(--text-tertiary)]">
            {[["Created", sub.created], ["Runs", sub.runs], ["Last run", sub.lastRun]].map(([k, v]) => (
              <span key={k} className="flex items-baseline gap-1.5 min-w-0">
                <span className="text-[12px] text-[var(--text-quaternary)]">{k}</span>
                <span className="font-mono tabular-nums truncate">{v}</span>
              </span>
            ))}
            <span className="flex-1" />
            <span className="text-[11px] text-[var(--text-quaternary)]">Managed by {parentName}</span>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}

function SubAgentRow({ sub, parentName, expanded, onToggle, onOpenDetail }: {
  sub: SubAgent;
  parentName: string;
  expanded: boolean;
  onToggle: () => void;
  onOpenDetail: () => void;
}) {
  return (
    <div className={`min-w-0 transition-colors ${expanded ? "bg-[var(--accent-hover)]" : "hover:bg-[var(--accent-hover)]"}`}>
      <button onClick={onToggle} aria-expanded={expanded} className="press w-full flex items-center gap-3 px-4 py-3 text-left min-w-0">
        <PixelAvatar seed={sub.seed} hue={sub.hue} size={32} rounded={8} className="shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="text-[13.5px] font-medium tracking-[-0.005em] truncate text-[var(--text-primary)]">{sub.name}</span>
            <span className="shrink-0 h-[17px] px-[6px] rounded-full ring-1 ring-inset ring-[var(--hairline-strong)] bg-[var(--surface-2)] text-[10px] font-mono text-[var(--text-secondary)] flex items-center">
              {sub.model}
            </span>
          </span>
          {!expanded && (
            <span className="block text-[12.5px] text-[var(--text-tertiary)] truncate mt-[1px]">{sub.desc}</span>
          )}
        </span>
        <ChevronDown className={`w-3 h-3 shrink-0 text-[var(--text-quaternary)] transition-transform ${expanded ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {expanded && (
        <div className="fade-in pl-[60px] pr-4 pb-4 space-y-2.5">
          <div className="text-[12.5px] leading-[1.55] text-[var(--text-secondary)] tracking-[-0.005em]">{sub.desc}</div>
          <div>
            <div className="flex items-center gap-1 mb-1 section-label">
              <Sparkles className="w-[9px] h-[9px]" strokeWidth={2} />
              Instructions · written by {parentName}
            </div>
            {/* Clamped preview — full text lives in the detail dialog */}
            <button
              onClick={onOpenDetail}
              title="Show full instructions"
              className="group/instr w-full text-left rounded-[7px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] px-2.5 py-2 hover:ring-[var(--hairline-strong)] transition-shadow"
            >
              <span
                className="block text-[12px] leading-[1.6] text-[var(--text-secondary)] whitespace-pre-wrap break-words"
                style={{ display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}
              >
                {sub.instructions}
              </span>
              <span className="mt-1 hidden group-hover/instr:flex items-center gap-1 text-[11px] font-medium text-[var(--text-tertiary)]">
                <ExternalLink className="w-[9px] h-[9px]" strokeWidth={2} /> Show full instructions
              </span>
            </button>
          </div>
          <div className="rounded-[7px] ring-1 ring-[var(--hairline)] overflow-hidden">
            <div className="grid grid-cols-3 gap-[1px] bg-[var(--hairline)]">
              {[["Created", sub.created], ["Runs", sub.runs], ["Last run", sub.lastRun]].map(([k, v]) => (
                <span key={k} className="flex flex-col gap-[1px] px-2 py-[5px] bg-[var(--surface)] min-w-0">
                  <span className="text-[12px] text-[var(--text-quaternary)]">{k}</span>
                  <span className="text-[11.5px] font-mono tabular-nums text-[var(--text-secondary)] truncate">{v}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Read-only: the main agent creates and manages its sub-agents. */
export default function SubAgentsSection({ agent, onUpdateAgent, onHelp }: {
  agent: Agent;
  onUpdateAgent: (updater: (a: Agent) => Agent) => void;
  onHelp: (section: ConfigSection) => void;
}) {
  const [expandedSubId, setExpandedSubId] = useState<string | null>(null);
  const [detailSub, setDetailSub] = useState<SubAgent | null>(null);
  const subAgents = agent.subAgents || [];
  const enabled = !!agent.capabilities?.subAgentsEnabled;
  const toggle = () => onUpdateAgent((a) => ({ ...a, capabilities: { ...a.capabilities, subAgentsEnabled: !enabled } }));

  return (
    <>
      <Section section="subagents" onHelp={onHelp} enabled={enabled} onToggle={toggle}>
        <ListCard label={<Counted n={subAgents.length} noun="sub-agent" />}>
          {subAgents.length === 0 && (
            <ListEmpty icon={Bot} title="No sub-agents yet" hint="The agent creates them when a task needs parallel help." />
          )}
          {subAgents.map((s) => (
            <SubAgentRow
              key={s.id}
              sub={s}
              parentName={agent.name}
              expanded={expandedSubId === s.id}
              onToggle={() => setExpandedSubId(expandedSubId === s.id ? null : s.id)}
              onOpenDetail={() => setDetailSub(s)}
            />
          ))}
        </ListCard>
        <Note>Created and managed by {agent.name} — edit its instructions to change how it delegates.</Note>
      </Section>
      <SubAgentDialog sub={detailSub} parentName={agent.name} onOpenChange={(o) => !o && setDetailSub(null)} />
    </>
  );
}
